"use client";

import { useRef, useState } from "react";
import { ChevronDownIcon, Loader2 } from "lucide-react";
import { useHotkeys } from "react-hotkeys-hook";

import type { SupportedLang } from "@acme/validators";
import { cn } from "@acme/ui";
import { Button } from "@acme/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@acme/ui/dropdown-menu";
import { TruncatedTooltip } from "@acme/ui/truncated-tooltip";

import { StudioIcon } from "~/app/_components/icons/sidebar-icons";
import { useSession } from "~/utils/auth";
import { useSessionActions } from "../_hooks/use-session-actions";
import { useSessions } from "../_hooks/use-sessions";
import { useStudioStore } from "../_hooks/use-studio-store";
import { SessionDialogs } from "./action-bar/session-dialogs";
import { SessionHistoryDropdownItem } from "./project-dropdown/session-history-dropdown-item";
import { SessionMenuItems } from "./project-dropdown/session-menu-items";
import { useShareModal } from "./project-dropdown/share-modal";
import { ViewDropdownItem } from "./project-dropdown/view-dropdown-item";

export function ProjectDropdown() {
  const userSession = useSession();
  const [, setShareModalOpen] = useShareModal();

  const {
    sessions,
    createSession,
    deleteSession,
    updateSession,
    isCreatingSession,
    isUpdatingSession,
    isDeletingSession,
  } = useSessions();

  const lang = useStudioStore((state) => state.lang);
  const dirty = useStudioStore((state) => state.dirty);
  const getCurrentState = useStudioStore((state) => state.getCurrentState);
  const loadSessionState = useStudioStore((state) => state.loadSessionState);
  const currentSessionId = useStudioStore((state) => state.currentSessionId);
  const setCurrentSessionId = useStudioStore(
    (state) => state.setCurrentSessionId,
  );
  const resetState = useStudioStore((state) => state.resetState);

  const [open, setOpen] = useState(false);
  const selectedLanguage = useRef<SupportedLang>(lang);

  const {
    actionLoading,
    isCreateDialogOpen,
    setIsCreateDialogOpen,
    isConfirmNewSessionOpen,
    setIsConfirmNewSessionOpen,
    isConfirmDeleteOpen,
    setIsConfirmDeleteOpen,
    setSessionToDelete,
    sessionToLoad,
    setSessionToLoad,
    handleNewSessionClick,
    handleSaveSession,
    handleCreateSession,
    handleDeleteSession,
    handleConfirmDeleteSession,
    handleItemClick,
  } = useSessionActions({
    currentSessionId,
    dirty,
    userSession,
    lang,
    selectedLanguage,
    resetState,
    setCurrentSessionId,
    getCurrentState,
    createSession: async (name, lang, state) => {
      await createSession(name, lang, state);
    },
    updateSession,
    deleteSession,
    setOpen,
  });

  useHotkeys("mod+alt+s", () => setIsCreateDialogOpen(true));

  const currentSession = sessions.find((s) => s.id === currentSessionId);
  const displayName = currentSession?.name ?? "Untitled Project";
  const hasUnsavedChanges = dirty;
  const isLoading = isCreatingSession || isUpdatingSession || isDeletingSession;

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant={"ghost"}
            disabled={isLoading}
            size="xs"
            className={cn(
              "group relative focus:ring-0 focus:ring-offset-0",
              open && "bg-muted",
            )}
            aria-label={`Session: ${displayName}${hasUnsavedChanges ? " (unsaved changes)" : ""}`}
          >
            {isLoading ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <StudioIcon className="size-4" />
            )}

            <TruncatedTooltip
              tooltipClassName="truncate"
              className="text-foreground hidden truncate xl:block"
            >
              {displayName}
            </TruncatedTooltip>

            {hasUnsavedChanges && (
              <div
                className="ring-background absolute -top-0.5 -right-0.5 size-2 animate-pulse rounded-full bg-amber-500 ring-1"
                aria-label="Unsaved changes"
              />
            )}

            <ChevronDownIcon className="shrink-0 transition-transform group-data-[state=open]:rotate-180" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-54 rounded-lg text-sm shadow-lg dark:shadow-2xl"
        >
          <SessionMenuItems
            onNewSession={handleNewSessionClick}
            onSaveSession={handleSaveSession}
            onDeleteSession={handleConfirmDeleteSession}
            onShare={() => {
              setOpen(false);
              void setShareModalOpen(true);
            }}
            onItemClick={handleItemClick}
            canSave={dirty}
            canDelete={!!currentSessionId}
            hasUnsavedChanges={hasUnsavedChanges}
            actionLoading={actionLoading}
          />

          <DropdownMenuSeparator />

          <SessionHistoryDropdownItem
            currentSessionId={currentSessionId}
            onDeleteSession={(sessionId) => {
              setSessionToDelete(sessionId);
              setIsConfirmDeleteOpen(true);
            }}
            onLoadSession={(sessionId, state) => {
              if (
                !currentSessionId &&
                dirty &&
                userSession.status === "authenticated"
              ) {
                setSessionToLoad({ id: sessionId, state });
                setIsConfirmNewSessionOpen(true);
              } else {
                loadSessionState(state);
                setCurrentSessionId(sessionId);
              }
            }}
          />

          <DropdownMenuSeparator />

          <ViewDropdownItem />
        </DropdownMenuContent>
      </DropdownMenu>

      <SessionDialogs
        isCreateDialogOpen={isCreateDialogOpen}
        setIsCreateDialogOpen={setIsCreateDialogOpen}
        onCreateSession={handleCreateSession}
        isCreatingSession={isCreatingSession}
        isConfirmNewSessionOpen={isConfirmNewSessionOpen}
        setIsConfirmNewSessionOpen={setIsConfirmNewSessionOpen}
        onStartNewSession={() => {
          resetState(selectedLanguage.current);
          setCurrentSessionId(null);
          setIsConfirmNewSessionOpen(false);
        }}
        onLoadSessionAfterConfirmation={() => {
          if (sessionToLoad) {
            loadSessionState(sessionToLoad.state);
            setCurrentSessionId(sessionToLoad.id);
            setSessionToLoad(null);
          }
          setIsConfirmNewSessionOpen(false);
        }}
        onSaveBeforeNewSession={() => {
          setIsConfirmNewSessionOpen(false);
          setIsCreateDialogOpen(true);
        }}
        hasSessionToLoad={!!sessionToLoad}
        isConfirmDeleteOpen={isConfirmDeleteOpen}
        setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
        onDeleteSession={handleDeleteSession}
        onCancelDelete={() => {
          setSessionToDelete(null);
          setIsConfirmDeleteOpen(false);
        }}
        isDeletingSession={isDeletingSession}
      />
    </>
  );
}
